import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import {
  getUsuarioPermisoDetailThunk,
  getPermisosDisponiblesThunk,
  syncUsuarioGruposThunk,
  syncUsuarioPermisosEspecialesThunk,
} from "../../store/rolesPermisos/rolesPermisos.thunks";
import { clearUsuarioDetail, clearError } from "../../store/rolesPermisos/rolesPermisos.slice";
import Loader from "../UI/Loader";

const UsuarioPermisosModal = ({ usuario, onClose }) => {
  const dispatch = useDispatch();
  const {
    usuarioDetail,
    usuarioDetailLoading,
    grupos,
    permisosDisponibles,
    permisosLoading,
    syncUsuarioLoading,
    error,
  } = useSelector((s) => s.rolesPermisos);

  const [tab, setTab] = useState("grupos");
  const [selectedGrupos, setSelectedGrupos] = useState(new Set());
  const [selectedPermisos, setSelectedPermisos] = useState(new Set());
  const [permSearch, setPermSearch] = useState("");
  const [successGrupos, setSuccessGrupos] = useState(false);
  const [successPermisos, setSuccessPermisos] = useState(false);

  useEffect(() => {
    dispatch(getUsuarioPermisoDetailThunk(usuario.id));
    if (!permisosDisponibles.length) {
      dispatch(getPermisosDisponiblesThunk());
    }
    return () => dispatch(clearUsuarioDetail());
  }, [dispatch, usuario.id]);

  useEffect(() => {
    if (usuarioDetail) {
      setSelectedGrupos(new Set((usuarioDetail.grupos ?? []).map((g) => g.id)));
      setSelectedPermisos(new Set((usuarioDetail.permisos_especiales ?? []).map((p) => p.id)));
    }
  }, [usuarioDetail]);

  const filteredApps = useMemo(() => {
    if (!permSearch.trim()) return permisosDisponibles;
    const q = permSearch.toLowerCase();
    return permisosDisponibles
      .map((app) => ({
        ...app,
        permisos: app.permisos.filter(
          (p) => p.codename.includes(q) || p.name.toLowerCase().includes(q)
        ),
      }))
      .filter((app) => app.permisos.length > 0);
  }, [permisosDisponibles, permSearch]);

  const toggleGrupo = (id) => {
    setSelectedGrupos((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSuccessGrupos(false);
  };

  const togglePerm = (id) => {
    setSelectedPermisos((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSuccessPermisos(false);
  };

  const toggleApp = (appPermisos) => {
    const ids = appPermisos.map((p) => p.id);
    const allChecked = ids.every((id) => selectedPermisos.has(id));
    setSelectedPermisos((prev) => {
      const next = new Set(prev);
      if (allChecked) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
    setSuccessPermisos(false);
  };

  const handleSaveGrupos = async () => {
    dispatch(clearError());
    const result = await dispatch(
      syncUsuarioGruposThunk({ id: usuario.id, grupoIds: [...selectedGrupos] })
    );
    if (!result.error) setSuccessGrupos(true);
  };

  const handleSavePermisos = async () => {
    dispatch(clearError());
    const result = await dispatch(
      syncUsuarioPermisosEspecialesThunk({
        id: usuario.id,
        permisoIds: [...selectedPermisos],
      })
    );
    if (!result.error) setSuccessPermisos(true);
  };

  const isLoading = usuarioDetailLoading || permisosLoading;
  const totalDisponibles = useMemo(
    () => permisosDisponibles.reduce((acc, g) => acc + g.permisos.length, 0),
    [permisosDisponibles]
  );

  const nombreCompleto = usuarioDetail
    ? `${usuarioDetail.nombres ?? ""} ${usuarioDetail.apellido_paterno ?? ""}`.trim() ||
      usuarioDetail.username
    : usuario.username ?? "";

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <Icon icon="mdi:account-key-outline" className="me-2" />
              Permisos de: <strong>{nombreCompleto}</strong>
              {usuarioDetail && (
                <span className="badge bg-neutral-200 text-neutral-600 ms-2 fw-normal">
                  {usuarioDetail.rol}
                </span>
              )}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {isLoading ? (
              <Loader center text="Cargando datos..." delay={0} />
            ) : (
              <>
                {error && <div className="alert alert-danger py-2">{error}</div>}

                {/* Tabs */}
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${tab === "grupos" ? "active" : ""}`}
                      onClick={() => setTab("grupos")}
                    >
                      <Icon icon="mdi:account-group-outline" className="me-1" />
                      Grupos ({selectedGrupos.size})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${tab === "permisos" ? "active" : ""}`}
                      onClick={() => setTab("permisos")}
                    >
                      <Icon icon="mdi:shield-key-outline" className="me-1" />
                      Permisos especiales ({selectedPermisos.size})
                    </button>
                  </li>
                  {usuarioDetail && (
                    <li className="nav-item ms-auto d-flex align-items-center">
                      <span className="text-muted text-sm">
                        Permisos efectivos totales: <strong>{usuarioDetail.permisos_efectivos?.length ?? 0}</strong>
                      </span>
                    </li>
                  )}
                </ul>

                {/* Tab: Grupos */}
                {tab === "grupos" && (
                  <div>
                    {successGrupos && (
                      <div className="alert alert-success py-2 mb-3">
                        <Icon icon="mdi:check-circle" className="me-1" />
                        Grupos guardados correctamente.
                      </div>
                    )}
                    <p className="text-muted text-sm mb-3">
                      Selecciona los grupos que tendrá este usuario. Heredará todos los permisos de cada grupo asignado.
                    </p>
                    <div className="row g-3">
                      {grupos.map((g) => (
                        <div key={g.id} className="col-12 col-md-4">
                          <label
                            className="d-flex align-items-start gap-3 p-3 rounded cursor-pointer w-100"
                            style={{
                              border: "2px solid",
                              borderColor: selectedGrupos.has(g.id) ? "#7749F8" : "#e5e7eb",
                              background: selectedGrupos.has(g.id) ? "#f5f0ff" : "white",
                              transition: "all 0.15s",
                            }}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input mt-1 flex-shrink-0"
                              checked={selectedGrupos.has(g.id)}
                              onChange={() => toggleGrupo(g.id)}
                            />
                            <div>
                              <div className="fw-semibold">{g.name}</div>
                              <div className="text-muted text-sm">{g.total_permisos} permisos</div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveGrupos}
                        disabled={syncUsuarioLoading}
                      >
                        {syncUsuarioLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <Icon icon="mdi:content-save-outline" className="me-1" />
                            Guardar grupos
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Tab: Permisos especiales */}
                {tab === "permisos" && (
                  <div>
                    {successPermisos && (
                      <div className="alert alert-success py-2 mb-3">
                        <Icon icon="mdi:check-circle" className="me-1" />
                        Permisos especiales guardados correctamente.
                      </div>
                    )}
                    <p className="text-muted text-sm mb-3">
                      Permisos individuales asignados directamente a este usuario, además de los heredados por sus grupos.
                    </p>
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Buscar permiso..."
                        value={permSearch}
                        onChange={(e) => setPermSearch(e.target.value)}
                      />
                      <span className="badge bg-primary-100 text-primary-600 fw-semibold text-nowrap">
                        {selectedPermisos.size} / {totalDisponibles}
                      </span>
                    </div>

                    <div style={{ maxHeight: "45vh", overflowY: "auto" }}>
                      {filteredApps.map((appGroup) => {
                        const appIds = appGroup.permisos.map((p) => p.id);
                        const allAppChecked = appIds.every((id) => selectedPermisos.has(id));
                        const someAppChecked = appIds.some((id) => selectedPermisos.has(id));

                        return (
                          <div key={appGroup.app} className="mb-3">
                            <div
                              className="d-flex align-items-center gap-2 p-2 rounded cursor-pointer"
                              style={{ background: "#f8f9fa", userSelect: "none" }}
                              onClick={() => toggleApp(appGroup.permisos)}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={allAppChecked}
                                ref={(el) => {
                                  if (el) el.indeterminate = someAppChecked && !allAppChecked;
                                }}
                                onChange={() => {}}
                              />
                              <span className="fw-semibold text-primary-600 text-sm">{appGroup.app}</span>
                              <span className="badge bg-neutral-200 text-neutral-600 ms-auto">
                                {appIds.filter((id) => selectedPermisos.has(id)).length} / {appIds.length}
                              </span>
                            </div>
                            <div className="row g-1 ms-1 mt-1">
                              {appGroup.permisos.map((perm) => (
                                <div key={perm.id} className="col-12 col-md-6 col-lg-4">
                                  <label
                                    className="d-flex align-items-start gap-2 p-2 rounded cursor-pointer"
                                    style={{
                                      fontSize: "0.8rem",
                                      border: "1px solid",
                                      borderColor: selectedPermisos.has(perm.id) ? "#7749F8" : "#e5e7eb",
                                      background: selectedPermisos.has(perm.id) ? "#f5f0ff" : "white",
                                      transition: "all 0.15s",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      className="form-check-input mt-0 flex-shrink-0"
                                      checked={selectedPermisos.has(perm.id)}
                                      onChange={() => togglePerm(perm.id)}
                                    />
                                    <span>
                                      <span className="d-block text-neutral-500" style={{ fontSize: "0.7rem", fontFamily: "monospace" }}>
                                        {perm.codename}
                                      </span>
                                      <span className="text-neutral-700">{perm.name}</span>
                                    </span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="d-flex justify-content-end mt-3">
                      <button
                        className="btn btn-primary"
                        onClick={handleSavePermisos}
                        disabled={syncUsuarioLoading}
                      >
                        {syncUsuarioLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <Icon icon="mdi:content-save-outline" className="me-1" />
                            Guardar permisos especiales
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsuarioPermisosModal;
